import React, { useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { useAlert } from "react-alert"

import BaseModal from "@components/modals/components/BaseModal/BaseModal"
import ModalTitle from "@components/modals/components/ModalTitle/ModalTitle"
import useModal from "@components/modals/useModal"
import Button from "@components/ui/Button/Button"
import Text from "@components/ui/Typography/Text"
import Templates from "@components/ui/Templates"
import { useController, useStore } from "@store"
import { MANUAL_UPLOAD_FILE_STATUS, ManualUploadFileInfo } from "@framework/types/content-manager"
import { getUniqueNameForFile } from "@utils/file"
import Select from "@components/ui/MultiSelect/Select"

import { ModalsTypes } from "../constants"
import SelectedFile from "./SelectedFile"
import DocumentDropZoneContainer from "./DocumentDropZoneContainer"

import styles from "./ContentManagerUploadFormModal.module.sass"

export type ContentManagerUploadFormModalProps = {
  label: string
  onSubmit: () => void
}

const ContentManagerUploadFormModal: React.FC<ContentManagerUploadFormModalProps> =
  observer(({ label, onSubmit }) => {
    const [state, setState] = useState<{
      files: File[]
      contentType?: string
      companyIds: string[]
      fileContentTypes: Record<string, string>
    }>({
      companyIds: [],
      files: [],
      fileContentTypes: {},
    })
    const [loading, setLoading] = useState(false)
    const alert = useAlert()

    const { hideModal } = useModal(
      ModalsTypes.CONTENT_MANAGER_UPLOAD_FORM_MODAL
    )

    const {
      contentManagerStore,
      datatypesStore: { data: allContentTypes },
    } = useStore()
    const {
      contentManagerController,
      datatypesController: { loadAllDataTypes },
    } = useController()

    const { contentType, files, fileContentTypes } = state
    const { companies } = contentManagerStore || {}

    const getCurrentUploads = (): ManualUploadFileInfo[] =>
      files
        .map((file) => contentManagerStore.uploads[getUniqueNameForFile(file)])
        .filter((upload): upload is ManualUploadFileInfo => Boolean(upload))

    const formatFailedUploadDetails = (failedUploads: ManualUploadFileInfo[]) =>
      failedUploads
        .map(
          (upload) =>
            `${upload.fileName}: ${upload.error || "Upload failed"}`
        )
        .join("\n")

    const handleFileUpload = async () => {
      const filesToUpload = files.filter(
        (file) =>
          contentManagerStore?.uploads[getUniqueNameForFile(file)]?.status !==
            MANUAL_UPLOAD_FILE_STATUS.COMPLETED &&
          contentManagerStore?.uploads[getUniqueNameForFile(file)]?.status !==
            MANUAL_UPLOAD_FILE_STATUS.UPLOADING
      )
      if (!filesToUpload.length) return

      setLoading(true)
      try {
        await Promise.all(
          filesToUpload.map(async (file) => {
            await contentManagerController.uploadDocument(
              file,
              contentType,
              state.companyIds
            )
          })
        )

        const failedUploads = getCurrentUploads().filter(
          (upload) => upload.status === MANUAL_UPLOAD_FILE_STATUS.FAILED
        )
        if (failedUploads.length) {
          alert.error(formatFailedUploadDetails(failedUploads))
        }
      } finally {
        setLoading(false)
      }
    }

    const onFilesDrop = (newFiles: File[]) => {
      const existingFiles = files.map((file) => {
        return file.name
      })

      setState({ ...state, fileContentTypes })
      const uniqueFiles = newFiles.filter((file) => {
        const cType = allContentTypes?.find((ct) => ct.id === contentType)
        fileContentTypes[file.name] = cType?.name || ""
        return !existingFiles.includes(file.name)
      })

      setState({
        ...state,
        files: [...uniqueFiles, ...files],
      })
    }

    useEffect(() => {
      handleFileUpload()
    }, [state.files])

    useEffect(() => {
      contentManagerStore.uploads = {}
      contentManagerController.getCompanies()
      loadAllDataTypes()

      return () => {
        contentManagerStore.uploads = {}
      }
    }, [])

    const currentUploads = getCurrentUploads()
    const completedUploads = currentUploads.filter(
      (upload) => upload.status === MANUAL_UPLOAD_FILE_STATUS.COMPLETED
    )
    const failedUploads = currentUploads.filter(
      (upload) => upload.status === MANUAL_UPLOAD_FILE_STATUS.FAILED
    )
    const hasUploadingFiles = currentUploads.some(
      (upload) => upload.status === MANUAL_UPLOAD_FILE_STATUS.UPLOADING
    )

    const handleCancel = () => {
      contentManagerStore.uploads = {}
      hideModal()
    }

    const handleDone = () => {
      if (failedUploads.length > 0) {
        alert.error(formatFailedUploadDetails(failedUploads))
      } else if (completedUploads.length > 0) {
        alert.success(
          "Documents are uploaded successfully, it may take few minutes to show in a list"
        )
        onSubmit()
      }

      contentManagerStore.uploads = {}
      hideModal()
    }

    const companiesOptions = React.useMemo(
      () =>
        companies?.map((item) => ({
          name: item.id,
          value: item.name,
        })) ?? [],
      [companies]
    )

    const contentTypeOptions = React.useMemo(
      () =>
        allContentTypes?.map((item) => ({
          name: item.id,
          value: item.name,
        })) ?? [],
      [allContentTypes]
    )

    return (
      <BaseModal
        className={styles.root}
        title={
          <ModalTitle
            titleText={<div className={styles.titleContainer}>{label}</div>}
          />
        }
        containerClassName={styles.container}
      >
        <Templates.RollScript
          footerSocket={
            <div className={styles.footer}>
              <Button variant="outlined" size="big" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={loading || hasUploadingFiles || !files.length}
                size="big"
                onClick={handleDone}
              >
                Done
              </Button>
            </div>
          }
        >
          <div className={styles.inputsWrapper}>
            <div className={styles.inputContainer}>
              <Text variant="body2">Manufacturers *</Text>
              <Select
                isMulti
                options={companiesOptions}
                placeholder="Select Manufacturers"
                value={state.companyIds}
                onChange={(selectedItems) => {
                  setState({
                    ...state,
                    companyIds: [...selectedItems],
                  })
                }}
              />
            </div>
            <div className={styles.inputContainer}>
              <Text variant="body2">Content Type</Text>
              <Select
                options={contentTypeOptions}
                placeholder="Select content type"
                value={state.contentType ?? null}
                onChange={(contentTypeId) => {
                  setState({
                    ...state,
                    contentType: contentTypeId ?? "",
                  })
                }}
              />
            </div>
            <Text variant="body2">
              <b>Note</b>: Content type can be updated at a later stage once you
              have uploaded the contents.
            </Text>
            <DocumentDropZoneContainer
              disabled={!state.companyIds}
              onFilesDrop={onFilesDrop}
            />
            <div className={styles.fileList}>
              {files.map((file) => (
                <SelectedFile
                  contentType={fileContentTypes[file.name]}
                  key={file.name}
                  file={file}
                />
              ))}
            </div>
          </div>
        </Templates.RollScript>
      </BaseModal>
    )
  })

export default ContentManagerUploadFormModal
